import React, { useRef, useEffect } from 'react';
import uniqueId from 'lodash/uniqueId';
import * as d3 from 'd3';
import { XAxis, YAxis, AreaChart, Area, ResponsiveContainer, Tooltip, ReferenceLine } from 'recharts';
import Show from 'src/shared/components/show/Show';
import { MoveUpRight, MoveDownRight, MoveRight } from 'lucide-react';
import { chartColors } from 'src/shared/constants/portfolio';
import TrendExplainer from 'src/shared/components/trend-explainer/TrendExplainer';
import { baselineFallback } from 'src/shared/helpers/baselineFallback';
import { cn } from 'src/shared/helpers/utils';

export type PortfolioAreaChartProps = {
    chartData: {
        value: number;
        year: number;
    }[];
    category?: React.ReactNode;
    chartHeight?: number;
    baselineYear?: number;
};

const PortfolioAreaChart = ({ chartData, category, chartHeight = 200, baselineYear }: PortfolioAreaChartProps) => {
    if (chartData.length === 0) {
        return (
            <div
                className='flex items-center justify-center text-sm text-grey-60'
                style={{ height: chartHeight }}
                data-testid='portfolio-area-chart-no-data'
            >
                No data
            </div>
        );
    }

    const totalRecords = chartData.length - 1;

    const latest = chartData[totalRecords];
    const baseline = chartData.find(point => point.year === baselineYear && point.year < latest.year) ?? chartData[0];
    const lastestValue = latest.value;
    const fallback = baselineFallback(
        chartData.map(point => point.year),
        baselineYear,
        baseline.year
    );

    const percentageIncrease = ((lastestValue - baseline.value) / baseline.value) * 100 || 0;
    const isTrendPositive = percentageIncrease > 0.1;
    const isTrendNeutral = percentageIncrease >= -0.1 && percentageIncrease <= 0.1;

    const gradientId = uniqueId();

    return (
        <div className='flex flex-col gap-2'>
            <div className='flex gap-3 items-center'>
                <div className='text-[42px] text-grey-80'>{lastestValue}</div>
                <div className='flex gap-[6px]'>
                    {category}
                    <div
                        className={cn(
                            'flex gap-2 px-2 py-1 rounded-2xl text-xs items-center',
                            fallback ? 'bg-yellow-0 ring-1 ring-inset ring-yellow-20' : 'bg-grey-0'
                        )}
                    >
                        {isTrendNeutral ? (
                            <MoveRight className='h-4 w-4' />
                        ) : (
                            <Show when={isTrendPositive} fallback={<MoveDownRight className='h-4 w-4' />}>
                                <MoveUpRight className='h-4 w-4' />
                            </Show>
                        )}
                        <div className='text-xs'>
                            <span className='inline-block min-w-[7ch] text-right tabular-nums'>{percentageIncrease.toFixed(2)}%</span>
                            <span className='text-grey-80'> since {baseline.year}</span>
                        </div>
                        <TrendExplainer baselineYear={baseline.year} comparisonYear={latest.year} fallback={fallback} />
                    </div>
                </div>
            </div>

            <div className='mb-4'>
                <ResponsiveContainer width='100%' height={chartHeight}>
                    <AreaChart data={chartData} margin={{ left: 10, right: 15 }}>
                        <defs>
                            <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                                <stop offset='5%' stopColor={chartColors.primary} stopOpacity={0.8} />
                                <stop offset='95%' stopColor={chartColors.primary} stopOpacity={0.2} />
                            </linearGradient>
                        </defs>
                        <XAxis dataKey='year' tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#666' }} dy={3} interval={1} />
                        <YAxis
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(tickValue: number) => tickValue.toFixed(2)}
                            tick={{ fontSize: 10, fill: '#666' }}
                            dx={-3}
                        />
                        <Tooltip />
                        <ReferenceLine
                            y={baseline.value}
                            stroke='#999'
                            strokeWidth={1.25}
                            strokeDasharray='10 3'
                            className={'text-xs text-gray-500'}
                            label={<CustomLabel label={baseline.value} />}
                        />
                        <Area type='monotone' dataKey='value' stroke={chartColors.primary} strokeWidth={2} fill={`url(#${gradientId})`} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const labelStyle = {
    fill: 'white',
    fontSize: '10px',
};

const CustomLabel = (props: any) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const textRef = useRef<SVGTextElement>(null);
    const rectRef = useRef<SVGRectElement>(null);

    useEffect(() => {
        if (!svgRef.current || !textRef.current || !rectRef.current) {
            return;
        }

        const textElement = d3.select(textRef.current);
        if (!textElement.node()) {
            return;
        }
        const rectElement = d3.select(rectRef.current);

        const textBBox = textElement?.node()?.getBBox();
        const rectWidth = textBBox ? textBBox.width + 10 : 0;
        const rectHeight = textBBox ? textBBox.height + 10 : 0;

        rectElement.attr('width', rectWidth).attr('height', rectHeight);
    }, [props]);

    return (
        <svg ref={svgRef}>
            <rect ref={rectRef} x={0} y={props.viewBox.y - 12} fill='#999' rx={4} ry={4} role='rect' />
            <text ref={textRef} x={5} y={props.viewBox.y} style={labelStyle} dominantBaseline='middle'>
                {props.label}
            </text>
        </svg>
    );
};

export default PortfolioAreaChart;
